import { MAX_UPLOAD_MB } from "./upload";

export const FIELD_MESSAGES = {
  campaignNameRequired: "Nome da campanha é obrigatório",
  campaignNameMax: (max: number) =>
    `Nome da campanha deve ter no máximo ${max} caracteres`,
  startDateRequired: "Data de início é obrigatória",
  endDateRequired: "Data de fim é obrigatória",
  storesMin: "Pelo menos uma loja deve ser selecionada",
  storesUnique: "Não é possível selecionar a mesma loja mais de uma vez",
  campaignIdRequired: "ID da campanha é obrigatório",
  customerFacingNameMax: (max: number) =>
    `Nome para o cliente deve ter no máximo ${max} caracteres`,
  customerFacingDescriptionMax: (max: number) =>
    `Texto abaixo do nome deve ter no máximo ${max} caracteres`,
  slugFormat: (min: number, max: number) =>
    `O link deve ter de ${min} a ${max} caracteres, só com letras minúsculas, números e hífen`,
} as const;

export const CAMPAIGN_LINK_COPY = {
  label: "Link da campanha",
  help: "Vira o endereço público da campanha no portal (/gameficacao/campanha/link). Sugerido a partir do nome; pode ser ajustado.",
  retry: "Tentar de novo",
  required: "O link da campanha é obrigatório",
  placeholder: "Ex: natal-premiado",
  reviewLabel: "Link da campanha",
  copyAction: "Copiar link",
  copied: "Link da campanha copiado!",
  copyFailed: "Não foi possível copiar o link. Copie manualmente.",
} as const;

export const SLUG_STATUS_COPY = {
  EMPTY: null,
  INVALID: null,
  CHECKING: "Conferindo se o link está livre...",
  AVAILABLE: "Link disponível.",
  TAKEN: "Já existe outra campanha com este link. Escolha outro.",
  FAILED: "Não foi possível conferir o link. Tente de novo.",
} as const;

export const SLUG_BLOCK_MESSAGES = {
  EMPTY: "Informe o link da campanha",
  INVALID: "Ajuste o formato do link da campanha",
  CHECKING: "Aguarde a conferência do link da campanha",
  AVAILABLE: null,
  TAKEN: "Escolha um link que nenhuma outra campanha use",
  FAILED: "Confira o link da campanha antes de continuar",
} as const;

export const CUSTOMER_FACING_COPY = {
  nameLabel: "Nome da campanha para o cliente",
  nameHelp:
    "É o nome exibido ao cliente no jogo. Se ficar em branco, usamos o nome da campanha.",
  namePlaceholder: "Ex: Roleta de Natal Avenida",
  descriptionLabel: "Texto abaixo do nome (para o cliente)",
  descriptionHelp:
    "Aparece abaixo do nome da campanha no portal. Deixe em branco para não exibir texto.",
  descriptionPlaceholder: "Ex: Gire a roleta e ganhe cupons nas Lojas Avenida.",
  coverLabel: "Capa do card na home (3:4)",
  coverRatio: "3:4",
  alternativeLabel: "Banner da página do jogo (16:9)",
  alternativeRatio: "16:9",
  bannersHint:
    "As duas imagens são obrigatórias: a capa (3:4) aparece no card do jogo na home e o banner (16:9) abre a página do jogo.",
} as const;

export const FILE_MESSAGES = {
  regulationPdf: "Regulamento deve ser um arquivo PDF",
  regulationMaxSize: `Arquivo deve ter no máximo ${MAX_UPLOAD_MB}MB`,
  imageMaxSize: `Imagem deve ter no máximo ${MAX_UPLOAD_MB}MB`,
  banner3x4Required: "A capa do card na home é obrigatória",
  banner16x9Required: "O banner da página do jogo é obrigatório",
  banner3x4Image: "A capa do card na home deve ser uma imagem",
  banner16x9Image: "O banner da página do jogo deve ser uma imagem",
  banner3x4File: "A capa do card na home deve ser um arquivo",
  banner16x9File: "O banner da página do jogo deve ser um arquivo",
} as const;

export const MECHANIC_RULE_MESSAGES = {
  namePrefixLimit: (prefix: string, limit: number, total: number) =>
    `Com o prefixo "${prefix} " o nome pode ter no máximo ${limit} caracteres (limite de ${total} no sistema)`,
  dateOrder: "A data de início deve ser anterior à data de fim",
  startDatePast: "A data de início não pode ser no passado",
  prizesFixedCount: (count: number) =>
    `Esta mecânica exige exatamente ${count} prêmios configurados`,
  prizesMin: "Cadastre ao menos um prêmio",
  prizesSum: "A soma das porcentagens deve ser exatamente 100%",
  concurrencyGroup: "Informe o grupo concorrente",
  voucherDays: "Informe a validade em dias (mínimo 1)",
  voucherStartOffset: "Dias para início deve ser ≥ 0",
  penaltyGoal: "Informe a probabilidade de gol entre 0 e 1",
  slotSymbols: "Cadastre ao menos um símbolo",
  slotSymbolFields: "Preencha o ID e o rótulo de cada símbolo",
  slotSymbolIcon: "Escolha um ícone do catálogo do caça-níquel",
  maxPlaysMin: "Jogadas por CPF deve ser um inteiro entre 1 e 999",
  minPurchaseMin: "Compra mínima deve ser ≥ 0",
  priorityRange: "Escolha uma prioridade: Baixa, Normal, Alta ou Máxima",
  segmentationFile: "Envie a base de CPFs ou desligue a segmentação",
  faqPair: "Preencha a pergunta e a resposta ou remova a linha",
} as const;

export const GAMIFICATION_SETTINGS_COPY = {
  openAction: "Configurações",
  title: "Configurações da gameficação",
  description: "Valem para todas as campanhas do portal.",
  radiusLabel: "Raio de proximidade das lojas (km)",
  radiusHelp:
    "Campanhas restritas a lojas aparecem no portal para clientes que estiverem a até esta distância de uma loja participante.",
  radiusRange: (min: number, max: number) =>
    `Informe um número inteiro entre ${min} e ${max} km`,
  loading: "Carregando configurações",
  loadError: "Não foi possível carregar as configurações.",
  retry: "Tentar novamente",
  cancel: "Cancelar",
  save: "Salvar",
  saving: "Salvando...",
  savedTitle: "Configurações salvas",
  savedDescription: (radius: number) =>
    `O raio de proximidade das lojas agora é de ${radius} km. O portal passa a usá-lo em até 1 minuto.`,
  saveErrorTitle: "Erro ao salvar",
  saveError: "Não foi possível salvar as configurações. Tente novamente.",
} as const;

export const COMPETING_CAMPAIGNS_COPY = {
  priorityLabel: "Prioridade",
  priorityHelp:
    "Define qual campanha vale quando outras do mesmo jogo estão no mesmo período.",
  missingPeriod:
    "Defina as datas de início e fim no passo “Mecânica e dados” para ver as campanhas concorrentes.",
  empty: "Nenhuma campanha concorre neste período",
  emptyPartial: "Nenhuma campanha concorrente encontrada entre as verificadas.",
  error: "Não foi possível carregar as campanhas concorrentes.",
  retry: "Tentar novamente",
  loading: "Carregando campanhas concorrentes",
  sameCustomerTitle: "Disputam o mesmo cliente",
  sameCustomerHelp: (group: string) =>
    `Campanhas do grupo "${group}", de qualquer jogo.`,
  sameGameTitle: "Disputam a vaga deste jogo",
  sameGameHelp: "Só uma campanha do mesmo jogo vale por vez.",
  position: (rank: number, total: number) =>
    `Esta campanha ficaria em ${rank}º de ${total}`,
  truncated: (limit: number) =>
    `Há mais de ${limit} campanhas no período; só as ${limit} primeiras foram verificadas.`,
} as const;

export const PRIORITY_CONCURRENCY_COPY = {
  title: "Prioridade e concorrência",
  ruleHintLabel: "Como a prioridade decide",
  priorityTab: "Prioridade",
  concurrencyTab: "Concorrência",
  competitorsTab: "Concorrentes",
  concurrencyParallel: "Paralela",
  concurrencyNoGroup: "Sem grupo",
  competitorsNoPeriod: "Defina o período",
  competitorsUnit: (count: number) => (count === 1 ? "campanha" : "campanhas"),
  tabHasError: "(tem erro)",
} as const;

export const MIN_PURCHASE_LOCK_COPY = {
  label: "Compra mínima (R$)",
  lockedHint: "Valor travado para edição",
  help: "Por enquanto a compra mínima é fixa em R$ 150,00 e não pode ser alterada.",
  tooltip:
    "Por enquanto este valor está travado para edição. Toda campanha nova usa compra mínima de R$ 150,00.",
} as const;

export const STEP_BLOCK_MESSAGES = {
  campaignName: "Preencha o nome da campanha",
  dates: "Preencha as datas de início e fim",
  stores: "Selecione ao menos uma loja",
  storeFields: "Preencha o nome e o endereço de todas as lojas",
  banners: "Envie a capa do card na home (3:4)",
  wideBanner: "Envie o banner da página do jogo (16:9)",
} as const;

export const BANNER_GALLERY_COPY = {
  open: "Escolher da galeria",
  openFor: (slot: string) => `Escolher da galeria: ${slot}`,
  title: "Galeria de imagens",
  description:
    "Escolha uma arte padrão ou a capa de outra campanha. A imagem é copiada para esta campanha.",
  sourceTabsLabel: "Origem das imagens",
  sourceDefault: "Padrões",
  sourceCampaign: "De outras campanhas",
  sourceAll: "Todas",
  gameFilterLabel: "Filtrar por jogo",
  allGames: "Todos os jogos",
  searchLabel: "Buscar pelo nome da campanha",
  searchPlaceholder: "Ex: Roleta de Natal",
  showAllAspects: "Mostrar todos os formatos",
  onlyAspect: (ratio: string) => `Mostrando só imagens ${ratio}.`,
  loading: "Carregando imagens da galeria",
  empty: "Nenhuma imagem encontrada",
  emptyHint: "Mude o filtro de origem, de jogo ou a busca.",
  error: "Não foi possível carregar a galeria.",
  retry: "Tentar novamente",
  badgeDefault: "Padrão",
  badgeCampaign: "Outra campanha",
  defaultArt: (game: string) => `Arte padrão · ${game}`,
  selectItem: (label: string, ratio: string) => `Usar ${label} (${ratio})`,
  selected: (label: string) => `Da galeria: ${label}`,
  imageAlt: (label: string) => `Imagem de capa: ${label}`,
} as const;

interface ToastMessage {
  variant: "default" | "destructive" | "information";
  description: string;
  duration?: number;
  closeOnClick?: boolean;
}

interface CampaignMessages {
  CREATE: {
    SUCCESS: ToastMessage;
    ERROR: ToastMessage;
  };
  UPDATE: {
    SUCCESS: ToastMessage;
    ERROR: ToastMessage;
  };
  INACTIVATE: {
    SUCCESS: ToastMessage;
    ERROR: ToastMessage;
  };
  HISTORY: {
    ERROR: ToastMessage;
    NO_ID: ToastMessage;
  };
  REPORT: {
    ERROR: ToastMessage;
  };
}

export const CAMPAIGN_MESSAGES: CampaignMessages = {
  CREATE: {
    SUCCESS: {
      variant: "default",
      description: "Campanha criada com sucesso!",
      duration: 5000,
      closeOnClick: true,
    },
    ERROR: {
      variant: "destructive",
      description: "Erro ao criar campanha. Tente novamente.",
      duration: 5000,
      closeOnClick: true,
    },
  },
  UPDATE: {
    SUCCESS: {
      variant: "default",
      description: "Campanha atualizada com sucesso!",
      duration: 5000,
      closeOnClick: true,
    },
    ERROR: {
      variant: "destructive",
      description: "Erro ao atualizar campanha. Tente novamente.",
      duration: 5000,
      closeOnClick: true,
    },
  },
  INACTIVATE: {
    SUCCESS: {
      variant: "default",
      description: "Campanha inativada com sucesso",
      duration: 5000,
      closeOnClick: true,
    },
    ERROR: {
      variant: "destructive",
      description: "Erro ao inativar campanha",
      duration: 5000,
      closeOnClick: true,
    },
  },
  HISTORY: {
    ERROR: {
      variant: "destructive",
      description: "Erro ao buscar o histórico da campanha. Tente novamente.",
      duration: 5000,
      closeOnClick: true,
    },
    NO_ID: {
      variant: "destructive",
      description: "Campanha sem IDCAPA — histórico indisponível.",
      duration: 5000,
      closeOnClick: true,
    },
  },
  REPORT: {
    ERROR: {
      variant: "destructive",
      description: "Erro ao carregar o relatório da campanha. Tente novamente.",
      duration: 5000,
      closeOnClick: true,
    },
  },
};

export const LOCAL_DRAFT_MESSAGES = {
  found: "Você tem um rascunho não salvo neste navegador.",
  foundAt: (quando: string) =>
    `Você tem um rascunho não salvo neste navegador, de ${quando}.`,
  scope: "Ele fica só nesta máquina e não aparece na listagem de campanhas.",
  missingFiles: (nomes: string[]) =>
    `Os anexos precisam ser reenviados: ${nomes.join(", ")}.`,
  segmentationOmitted:
    "Por segurança, a base de CPFs não é guardada neste navegador: importe-a novamente na etapa de segmentação.",
  segmentationRestoredTitle: "Base de CPFs não restaurada",
  segmentationReimport:
    "O rascunho não inclui a base de CPFs. Importe a base novamente na etapa de segmentação.",
  restore: "Retomar rascunho",
  discard: "Descartar rascunho",
  discardTitle: "Descartar rascunho?",
  discardDescription:
    "O rascunho salvo neste navegador será apagado e não poderá ser recuperado.",
  discardConfirm: "Descartar rascunho",
  discardCancel: "Manter rascunho",
} as const;

export const SEGMENTATION_COPY = {
  pendingBase:
    "Grave a base de CPFs na etapa de segmentação antes de salvar a campanha.",
  pendingNotice: "Há alterações na base de CPFs que ainda não foram gravadas.",
  importFailed: "Falha ao validar a base de CPFs. Tente novamente.",
} as const;

export const UPLOAD_COPY = {
  chooseBanner: (label: string) => `Clique para fazer upload: ${label}`,
  replaceBanner: (label: string) => `Trocar ${label}`,
  replaceExistingBanner: (label: string) =>
    `Banner já enviado, clique para substituir: ${label}`,
  chooseRegulation: "Clique para enviar o regulamento em PDF",
  replaceRegulation: "Substituir o arquivo do regulamento em PDF",
} as const;

export const UNSAVED_CHANGES_MESSAGES = {
  title: "Descartar alterações?",
  description:
    "Você tem alterações não salvas. Se sair agora, elas serão perdidas.",
  confirm: "Descartar e sair",
  cancel: "Continuar editando",
} as const;

export const CONCURRENCY_GROUP_COPY = {
  label: "Grupo concorrente *",
  triggerPlaceholder: "Selecione ou crie um grupo",
  searchPlaceholder: "Buscar ou digitar um grupo novo",
  searchLabel: "Buscar grupo concorrente",
  existingHeading: "Grupos existentes",
  createHeading: "Novo grupo",
  create: (name: string) => `Criar grupo "${name}"`,
  emptyNoGroups: "Nenhum grupo ainda — digite um nome para criar",
  emptyNoMatch: "Nenhum grupo encontrado — digite um nome para criar",
  loading: "Carregando grupos",
  error: "Não foi possível carregar os grupos.",
  retry: "Tentar novamente",
  countSummary: (campaigns: number, active: number) =>
    `${campaigns} ${campaigns === 1 ? "campanha" : "campanhas"} · ${active} ${active === 1 ? "ativa" : "ativas"}`,
  help:
    "Campanhas do mesmo grupo concorrem entre si (o cliente joga só uma no período). " +
    "Um grupo novo passa a existir quando a campanha é salva.",
  compositionTitle: (group: string) => `Campanhas no grupo "${group}"`,
  compositionNewGroupTitle: "Grupo novo",
  compositionNewGroup: "Esta será a primeira campanha do grupo.",
  compositionLoading: "Carregando campanhas do grupo",
  compositionError: "Não foi possível carregar as campanhas do grupo.",
  compositionInternalName: (name: string) => `Nome interno: ${name}`,
  compositionPeriod: (start: string, end: string) => `${start} a ${end}`,
  compositionMeta: (mechanic: string, period: string) => `${mechanic} · ${period}`,
  compositionNoPeriod: "Sem período",
} as const;
